import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type QuizAttempt = {
  id: string;
  quiz_slug: string;
  score: number;
  total: number;
  passed: boolean;
  created_at: string;
};

const LOCAL_KEY = 'lorescout_quiz_attempts';

function readLocal(): QuizAttempt[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? (JSON.parse(raw) as QuizAttempt[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(items: QuizAttempt[]) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(items.slice(-100)));
  } catch {
    // ignore
  }
}

export function useQuizAttempts() {
  const { user } = useAuth();
  const [attempts, setAttempts] = useState<QuizAttempt[]>(() => readLocal());

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('learn_quiz_attempts')
        .select('id, quiz_slug, score, total, passed, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(200);
      if (!active || !data) return;
      const merged = [...readLocal(), ...data];
      // de-dupe by id
      const seen = new Set<string>();
      const unique = merged.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
      setAttempts(unique);
      writeLocal(unique);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const recordAttempt = useCallback(
    async (quizSlug: string, score: number, total: number, passed: boolean) => {
      const local: QuizAttempt = {
        id:
          (typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `local-${Date.now()}`),
        quiz_slug: quizSlug,
        score,
        total,
        passed,
        created_at: new Date().toISOString(),
      };
      setAttempts((prev) => {
        const next = [local, ...prev];
        writeLocal(next);
        return next;
      });
      if (user) {
        const { data } = await supabase
          .from('learn_quiz_attempts')
          .insert({ user_id: user.id, quiz_slug: quizSlug, score, total, passed })
          .select('id, quiz_slug, score, total, passed, created_at')
          .single();
        if (data) {
          setAttempts((prev) => {
            // replace the optimistic row
            const next = prev.map((a) => (a.id === local.id ? (data as QuizAttempt) : a));
            writeLocal(next);
            return next;
          });
        }
      }
    },
    [user],
  );

  const bestForQuiz = useCallback(
    (quizSlug: string) => {
      const ours = attempts.filter((a) => a.quiz_slug === quizSlug);
      if (!ours.length) return null;
      return ours.reduce((best, cur) =>
        cur.score / cur.total > best.score / best.total ? cur : best,
      );
    },
    [attempts],
  );

  const hasPassed = useCallback(
    (quizSlug: string) => attempts.some((a) => a.quiz_slug === quizSlug && a.passed),
    [attempts],
  );

  return { attempts, recordAttempt, bestForQuiz, hasPassed };
}
